import { useState } from 'react';
import { generatePath, Link } from 'react-router-dom';
import { TaskView } from '@nexelem/graphne-api';
import { Badge } from '@nexelem/components';
import { useCustomTranslation } from '../../../../../../../../hooks';
import { getAbsolutePath, ROUTES } from '../../../../../../../../routes/config';
import { ProductionTaskCardForm } from '../../../formComponents/ProductionTaskCardForm';
import { ProductionTaskCard } from '../../ProductionTaskCard';
import { AssignedWorkersCard } from '../../AssignedWorkersCard';
import { formatUnit } from '../../../../../../../../components/organisms/ProductionPlans/Kanban/components/KanbanTask';
import { PRODUCT_AVAILABLE_PATH_TABS } from '../../../../../enums';
import { useAuthContext } from '../../../../../../../../providers/AuthProvider';
import { PERMS } from '../../../../../../../../routes/permissions';

type LotTabProps = {
  task: TaskView;
  isSuggested?: boolean;
  isEditable: boolean;
  errorMessage?: string;
};

export const LotTab = ({ task, isSuggested, isEditable, errorMessage }: LotTabProps) => {
  const {
    productDetails,
    progress: { totalAmount },
    orders,
    softResourceAssignments,
    tags = [],
  } = task;
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const { hasPermission } = useAuthContext();
  const canViewProductDetails = hasPermission(PERMS.PERM_8);
  const comments = orders?.flatMap((order) => order.comment)?.filter((item) => item) ?? [];

  const onEditToggle = () => setIsEditOpen((prevState) => !prevState);

  const handleOnSuccess = () => {
    setIsEditOpen(false);
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      {errorMessage && (
        <div className="flex flex-col gap-1 rounded-xs border border-border-state-error-very-light bg-surface-state-error-very-light p-4">
          <span className="text-t14-sb text-text-secondary">{tWrapper('error')}</span>
          <span className="text-s18 text-text-primary">{errorMessage}</span>
        </div>
      )}

      {!isEditOpen ? (
        <ProductionTaskCard
          isEditable={!!isEditable}
          isSuggested={isSuggested}
          task={task}
          onEditToggle={onEditToggle}
        />
      ) : (
        <ProductionTaskCardForm task={task} onCancel={onEditToggle} onSuccess={handleOnSuccess} />
      )}

      {tags.length > 0 && (
        <div className="flex flex-col gap-3">
          <span className="text-t14-sb text-text-secondary">{tWrapper('productionTags')}</span>
          <div className="flex flex-wrap gap-2">
            {tags.map(({ id, name }) => (
              <Badge key={`${id}-${name}`}>{name}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* TODO: when technology will be modified */}
      {/* {technology && (
        <div className="flex flex-col gap-3">
          <span className="text-t14-sb text-text-secondary">{tWrapper('technology')}</span>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon className="text-icon-primary" icon={faFlaskGear} />
            <span className="text-s18 text-text-primary">{technology}</span>
          </div>
        </div>
      )} */}

      <AssignedWorkersCard workers={softResourceAssignments} />

      {comments.length > 0 && (
        <div className="flex flex-col gap-2 rounded-xs border border-border-container bg-surface-container-lighter p-4">
          <span className="text-t14-sb text-text-secondary">{tWrapper('comment')}</span>
          <span className="text-s18 whitespace-pre-line text-text-primary">{comments.join('\n')}</span>
        </div>
      )}

      <div className="mt-6 flex flex-col">
        <span className="pb-4 text-t16-b">{tWrapper('product')}</span>
        <div className="flex flex-col gap-3 pb-7">
          <span className="text-t14-sb text-text-secondary">{tWrapper('productName')}</span>
          {canViewProductDetails ? (
            <Link
              className="text-s18 text-text-brand underline"
              rel="noopener noreferrer"
              target="_blank"
              to={getAbsolutePath(
                generatePath(ROUTES.products.details.full, {
                  id: productDetails.id.toString(),
                  tab: PRODUCT_AVAILABLE_PATH_TABS.PROPERTIES,
                }),
              )}
            >
              <span>{productDetails.name}</span>
            </Link>
          ) : (
            <span className="text-s18 text-text-primary">{productDetails.name}</span>
          )}
        </div>

        <div className="flex justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-t14-sb text-text-secondary">{tWrapper('productId')}</span>
            <span className="text-t18">{productDetails.code}</span>
          </div>
          <div className="flex flex-col gap-3 text-nowrap">
            <span className="text-t14-sb text-text-secondary">
              {tWrapper('amount')} ({totalAmount.unit.name})
            </span>
            <span className="text-t18">{formatUnit(totalAmount.amount)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
